import { FilterAlt } from "@mui/icons-material";
import {
  Alert,
  Autocomplete,
  InputAdornment,
  LinearProgress,
  TextField,
  Typography,
} from "@mui/material";
import { DataGridPro, GridToolbarContainer, GridToolbarExport } from "@mui/x-data-grid-pro";
import { uniq } from "lodash-es";
import { FC, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSupabase, useSupabaseCallback } from "../../../server/supabase/hooks";
import { UUID } from "../../../utils/uuid";
import { SupabaseSegmentService } from "../../segment/server/supabase-segment-service";
import { PersonWithManager, SupabasePersonService } from "../server/supabase-person-service";

type Audience = {
  id: UUID | null;
  name: string | null;
};
export const EmployeeDataGrid: FC<{
  refresh: boolean;
  type: "hris" | "csv";
  audiences: Audience[];
  onRefresh: () => void;
}> = ({ refresh, type, audiences, onRefresh }) => {
  const [employeeData, setEmployeeData] = useState<{
    data: { id: UUID | null; [key: string]: unknown }[];
    keys: string[];
  }>();
  const { t } = useTranslation();
  const [peopleInSegment, setPeopleInSegment] = useState<UUID[] | undefined>(undefined);
  const [selectedAudience, setSelectedAudience] = useState<UUID | null>(
    type === "hris"
      ? (audiences.find((x) => x.name === "Current employees and upcoming new hires")?.id ??
          audiences.find((x) => x.name === "Current employees")?.id ??
          null)
      : null,
  );
  const [error, setError] = useState(false);

  const updatePersonsInSegment = useSupabaseCallback(
    async ({ supabase }, audience: Audience | null) => {
      if (!audience?.id) {
        setPeopleInSegment(undefined);
        setSelectedAudience(null);
        return;
      }

      const { data } = await new SupabaseSegmentService(supabase).getCount(audience.id, false);
      if (!data) return;

      setPeopleInSegment(
        data.filter((x) => (x as { id: UUID }).id !== undefined).map((x) => (x as { id: UUID }).id),
      );
      setSelectedAudience(audience.id);
    },
    [],
  );

  useSupabase(
    async ({ supabase, account_id }) => {
      if (!refresh) return;
      setEmployeeData(undefined);

      const { data, error: loadError } = await new SupabasePersonService(
        supabase,
      ).getAllWithManager(account_id);
      if (loadError || !data) return setError(true);

      const textData = (person: PersonWithManager): { [key: string]: unknown } =>
        person.person_text_data !== null
          ? person.person_text_data
              .filter(({ source }) => source !== "CALCULATED")
              .reduce((o, e) => Object.assign(o, { [e.key]: e.value }), {})
          : {};

      const eventData = (person: PersonWithManager): { [key: string]: unknown } =>
        person.event !== null
          ? person.event
              .filter(({ source }) => source !== "CALCULATED")
              .reduce((o, e) => Object.assign(o, { [`event_${e.key}`]: e.date }), {})
          : {};

      const mappedData = data.map((person) => {
        return {
          id: person.id,
          ...textData(person),
          manager_employee_id: person.manager_employee_id,
          timezone: person.timezone,
          ...eventData(person),
        };
      });

      const keys: string[] = uniq(
        mappedData.reduce((prev, current) => [...prev, ...Object.keys(current)], [] as string[]),
      );

      setEmployeeData({ data: mappedData, keys });
      onRefresh();
    },
    [onRefresh, refresh],
  );

  if (!employeeData) return <LinearProgress />;

  if (error) return <Alert severity="warning">Unable to load employee data.</Alert>;

  const columns = [
    ...(employeeData.keys.includes("first_name")
      ? [{ field: "first_name", headerName: "first_name", valueFormatter }]
      : []),
    ...(employeeData.keys.includes("last_name")
      ? [{ field: "last_name", headerName: "last_name", valueFormatter }]
      : []),
    ...employeeData.keys
      .filter((x) => !["first_name", "last_name", "id"].includes(x))
      .map((x) => ({ field: x, headerName: x, valueFormatter })),
  ];

  return (
    <DataGridPro
      autoHeight
      columns={columns}
      rows={
        peopleInSegment
          ? employeeData.data.filter((x) => x.id && peopleInSegment.includes(x.id))
          : employeeData.data
      }
      slots={{
        toolbar: () => (
          <>
            {type === "csv" && selectedAudience && (
              <Alert severity="warning">
                <Typography variant="body2">
                  {t(
                    "The exported CSV will only contain a subset of your records, please do not import it as filtered records will be deleted.",
                  )}
                </Typography>
              </Alert>
            )}
            <GridToolbarContainer sx={{ justifyContent: "space-between" }}>
              <GridToolbarExport />
              {audiences && (
                <Autocomplete
                  value={audiences.find((x) => x.id === selectedAudience)}
                  options={audiences ?? []}
                  onChange={(_, x) => void updatePersonsInSegment(x).catch(console.error)}
                  sx={{ minWidth: "250px" }}
                  getOptionLabel={(option) => option.name ?? ""}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant="standard"
                      id="audience-filter"
                      placeholder={t("Audience")}
                      InputProps={{
                        ...params.InputProps,
                        startAdornment: (
                          <InputAdornment position="start">
                            <FilterAlt />
                          </InputAdornment>
                        ),
                      }}
                    />
                  )}
                />
              )}
            </GridToolbarContainer>
          </>
        ),
      }}
      slotProps={{ toolbar: { allColumns: true } }}
    />
  );
};

function valueFormatter<T>(value: T): T | string {
  return value ?? "";
}
